/* GASSISA
=========================================================================================
* Descripcion:
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 22/06/2026 | Lic. Jose Luis Aragon Cervantes  | Bootstrap tenant sin dashboard inicial
*      2       | 08/09/2026 | Lic. Jose Luis Aragon Cervantes  | Scroll vertical en activacion y recuperacion de equipo para celular y tableta
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Alert, Box, Button, Chip, CircularProgress, Divider, InputAdornment, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import PasswordOutlinedIcon from "@mui/icons-material/PasswordOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { tenantGateStatuses, useSesion } from "../../context/System/SesionContext";
import arsyncLogo from "../../img/arsync/LOGOARSYNCNEURONAL22052026.png";

const sx = {
  shell: {
    height: { xs: "100dvh", md: "100vh" },
    minHeight: { xs: "100dvh", md: "100vh" },
    overflowY: "auto",
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "420px 1fr" },
    background:
      "radial-gradient(circle at top left, rgba(0,188,212,.26), transparent 32%), linear-gradient(135deg,#07111f 0%,#0d1b2a 50%,#07111f 100%)",
  },
  brand: {
    color: "#fff",
    p: { xs: 3, md: 5 },
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 4,
    borderRight: { md: "1px solid rgba(255,255,255,.12)" },
  },
  brandTop: { display: "flex", alignItems: "center", gap: 2 },
  brandLogo: {
    width: 78,
    height: 78,
    objectFit: "contain",
    borderRadius: 3,
    background: "rgba(255,255,255,.08)",
    p: 1,
  },
  badgeWrap: { display: "flex", flexDirection: "column", gap: 1.5 },
  badge: {
    justifyContent: "flex-start",
    color: "#fff",
    borderColor: "rgba(255,255,255,.22)",
    backgroundColor: "rgba(255,255,255,.08)",
    backdropFilter: "blur(8px)",
  },
  workspace: {
    minHeight: { xs: "auto", md: "100vh" },
    display: "flex",
    alignItems: { xs: "flex-start", md: "center" },
    justifyContent: "center",
    p: { xs: 2, md: 3 },
    pb: { xs: 4, md: 3 },
  },
  panel: {
    width: "100%",
    maxWidth: 520,
    borderRadius: 5,
    p: { xs: 1.5, md: 2 },
    boxShadow: "0 28px 70px rgba(0,0,0,.38)",
    background: "rgba(255,255,255,.97)",
  },
  panelLogo: { width: 64, display: "block", mx: "auto", mb: 0.5 },
  statusBlock: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 1,
  },
  steps: { width: "100%", mt: 1 },
  form: { display: "flex", flexDirection: "column", gap: 1 },
  caption: { color: "text.secondary" },
  actionRow: {
    display: "flex",
    gap: 1.5,
    flexWrap: "wrap",
    justifyContent: "center",
    mt: 1,
  },
  actionRowEnd: {
    display: "flex",
    gap: 1.5,
    flexWrap: "wrap",
    justifyContent: "flex-end",
    mt: 1,
  },
};

const getSafeField = (form, fieldName) =>
  form?.[fieldName] ?? { value: "", error: false };

const getStepState = (status, isMobileTenant) => {
  const steps = isMobileTenant ? [
    { key: "device", label: "Identificando dispositivo portétil" },
    { key: "mfa", label: "Validando administrador" },
    { key: "security", label: "Validando equipo en Seguridad Tenant" },
    { key: "login", label: "Preparando Login SizeSoftX" },
  ] : [
    { key: "printertools", label: "Verificando PrinterTools" },
    { key: "machine", label: "Obteniendo nombre del equipo" },
    { key: "security", label: "Validando equipo en Seguridad Tenant" },
    { key: "login", label: "Preparando Login SizeSoftX" },
  ];

  let activeIndex = 0;

  if (status === tenantGateStatuses.LOOKUP_BY_NAME) activeIndex = 2;
  if (status === tenantGateStatuses.VALIDATING) activeIndex = 2;
  if (status === tenantGateStatuses.AUTHORIZED) activeIndex = 3;

  if (
    [
      tenantGateStatuses.NEEDS_ACTIVATION,
      tenantGateStatuses.MFA_REQUIRED,
      tenantGateStatuses.MFA_CONFIRMED,
      tenantGateStatuses.PRINTERTOOLS_REQUIRED,
      tenantGateStatuses.DENIED,
      tenantGateStatuses.PENDING_APPROVAL,
    ].includes(status)
  ) {
    activeIndex = 2;
  }

  return steps.map((step, index) => ({
    ...step,
    done: index < activeIndex || status === tenantGateStatuses.AUTHORIZED,
    active: index === activeIndex && status !== tenantGateStatuses.AUTHORIZED,
  }));
};

const getTitle = (status) => {
  if (status === tenantGateStatuses.PRINTERTOOLS_REQUIRED) return "PrinterTools requerido";
  if (status === tenantGateStatuses.NEEDS_ACTIVATION) return "Activación de equipo";
  if (status === tenantGateStatuses.MFA_REQUIRED) return "Confirmación MFA";
  if (status === tenantGateStatuses.MFA_CONFIRMED) return "Finalizar activación";
  if (status === tenantGateStatuses.DENIED) return "Acceso no disponible";
  if (status === tenantGateStatuses.PENDING) return "Preparando cliente SizeSoftX";
  if (status === tenantGateStatuses.PENDING_APPROVAL) return "Solicitud de acceso pendiente";
  return "Iniciando SizeSoftX";
};

export default function TenantActivationGate() {
  const [mfaQrDataUrl, setMfaQrDataUrl] = useState("");
  const [showPcActivationFields, setShowPcActivationFields] = useState(false);

  const {
    tenantActivationForm,
    tenantGateStatus,
    tenantGateMessage,
    tenantCorrelationId,
    tenantMfaSetup,
    handleTenantActivationFormChange,
    handleSubmitTenantActivation,
    handleRequestWorkstationAccess,
    handleConfirmTenantMfa,
    handleRedeemTenantActivation,
    handleRecoverMobileTenant,
    isRedeemingTenant,
    isRecoveringMobileTenant,
    isMobileTenant,
    mobileVerificationChallenge,
    handleRetryTenantGate,
    handleReassignTenantDevice,
    handleResetTenantActivation,
    handleDownloadPrinterTools,
    tenantDebugLog,
    clearTenantDebugLog,
    copyTenantDebugLog,
  } = useSesion();

  const licenseGuidField = getSafeField(tenantActivationForm, "licenseGuid");
  const companyNameField = getSafeField(tenantActivationForm, "companyName");
  const businessNameField = getSafeField(tenantActivationForm, "businessName");
  const adminNameField = getSafeField(tenantActivationForm, "adminName");
  const adminEmailField = getSafeField(tenantActivationForm, "adminEmail");
  const adminPhoneField = getSafeField(tenantActivationForm, "adminPhone");
  const deviceAliasField = getSafeField(tenantActivationForm, "deviceAlias");
  const adminPasswordField = getSafeField(tenantActivationForm, "adminPassword");
  const adminPasswordConfirmField = getSafeField(tenantActivationForm, "adminPasswordConfirm");
  const mfaCodeField = getSafeField(tenantActivationForm, "mfaCode");
  const claveActivacionField = getSafeField(tenantActivationForm, "claveActivacion");

  const isLoading = [
    tenantGateStatuses.BOOTSTRAP,
    tenantGateStatuses.LOOKUP_BY_NAME,
    tenantGateStatuses.VALIDATING,
    tenantGateStatuses.PROVISIONING,
  ].includes(tenantGateStatus);

  const showPrinterTools = tenantGateStatus === tenantGateStatuses.PRINTERTOOLS_REQUIRED;
  const showActivation = tenantGateStatus === tenantGateStatuses.NEEDS_ACTIVATION;
  const showMfa = tenantGateStatus === tenantGateStatuses.MFA_REQUIRED;
  const showRedeem = tenantGateStatus === tenantGateStatuses.MFA_CONFIRMED;
  const showDenied = tenantGateStatus === tenantGateStatuses.DENIED;
  const showReassign = tenantGateStatus === tenantGateStatuses.NEEDS_REASSIGNMENT;
  const showPendingApproval = tenantGateStatus === tenantGateStatuses.PENDING_APPROVAL;

  const steps = useMemo(() => getStepState(tenantGateStatus, isMobileTenant), [tenantGateStatus, isMobileTenant]);

  useEffect(() => {
    let mounted = true;

    if (!tenantMfaSetup?.otpAuthUri) {
      setMfaQrDataUrl("");
      return undefined;
    }

    QRCode.toDataURL(tenantMfaSetup.otpAuthUri, {
      errorCorrectionLevel: "M",
      margin: 2,
      scale: 6,
      color: { dark: "#07111f", light: "#ffffff" },
    })
      .then((dataUrl) => {
        if (mounted) setMfaQrDataUrl(dataUrl);
      })
      .catch(() => {
        if (mounted) setMfaQrDataUrl("");
      });

    return () => {
      mounted = false;
    };
  }, [tenantMfaSetup?.otpAuthUri]);

  const renderBootstrap = () => (
    <Box sx={sx.statusBlock}>
      <CircularProgress size={46} />
      <Typography component="h2" variant="h5" fontWeight={800}>
        {getTitle(tenantGateStatus)}
      </Typography>
      <Typography component="p" color="text.secondary">
        {tenantGateMessage || "Validando requisitos del equipo antes de abrir el acceso."}
      </Typography>

      <Box sx={{ width: "100%", mt: 1 }}>
        <LinearProgress />
      </Box>

      <Stack spacing={1.2} sx={sx.steps}>
        {steps.map((step) => (
          <Box key={step.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {step.done ? (
              <CheckCircleOutlineIcon color="success" />
            ) : step.active ? (
              <CircularProgress size={18} />
            ) : (
              <SecurityOutlinedIcon color="disabled" />
            )}
            <Typography component="span">{step.label}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );

  const renderPrinterTools = () => (
    <Box sx={sx.statusBlock}>
      <SecurityOutlinedIcon sx={{ fontSize: 52, color: "#0A5DA6" }} />
      <Typography component="h2" variant="h5" fontWeight={800}>
        PrinterTools requerido
      </Typography>
      <Typography component="p" color="text.secondary">
        Se requiere PrinterTools para obtener el nombre del equipo y validar la licencia.
      </Typography>

      <Alert severity="info" sx={{ width: "100%" }}>
        Instala PrinterTools, abre la aplicación local y después presiona Reintentar.
      </Alert>

      <Box sx={sx.actionRow}>
        <Button variant="contained" startIcon={<DownloadOutlinedIcon />} onClick={handleDownloadPrinterTools}>
          Descargar PrinterTools
        </Button>
        <Button variant="outlined" startIcon={<PublishedWithChangesOutlinedIcon />} onClick={handleRetryTenantGate}>
          Reintentar
        </Button>
      </Box>
    </Box>
  );

  const renderDenied = () => (
    <Box sx={sx.statusBlock}>
      <ErrorOutlineIcon sx={{ fontSize: 54, color: "#c62828" }} />
      <Typography component="h2" variant="h5" fontWeight={800}>
        Acceso no disponible
      </Typography>
      <Typography component="p" color="text.secondary">
        {tenantGateMessage || "No fue posible validar este equipo."}
      </Typography>

      {tenantCorrelationId && (
        <Alert severity="warning" sx={{ width: "100%" }}>
          Folio: {tenantCorrelationId}
        </Alert>
      )}

      <Box sx={sx.actionRow}>
        <Button variant="contained" startIcon={<PublishedWithChangesOutlinedIcon />} onClick={handleRetryTenantGate}>
          Reintentar
        </Button>
        <Button variant="outlined" startIcon={<RestartAltOutlinedIcon />} onClick={handleResetTenantActivation}>
          Nueva activación
        </Button>
      </Box>
    </Box>
  );

  const renderActivation = () => (
    <Box component="form" sx={sx.form} noValidate onSubmit={handleSubmitTenantActivation}>
      <Typography component="h2" variant="h5" fontWeight={800}>
        {isMobileTenant ? "Solicitar acceso" : (showPcActivationFields ? "Activar equipo" : "Solicitar acceso")}
      </Typography>
      <Typography component="p" sx={sx.caption}>
        {isMobileTenant
          ? "Captura la licencia y los datos del dispositivo. El propietario aprobaré la solicitud desde el dashboard administrativo."
          : (showPcActivationFields ? "Captura los datos completos para activar una licencia nueva con QR." : "Captura la licencia y los datos de esta estación. El propietario aprobaré el acceso desde el dashboard administrativo.")}
      </Typography>

      {tenantGateMessage && (
        <Alert severity="info">
          {tenantGateMessage}
        </Alert>
      )}

      {false && isMobileTenant && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: "primary.main" }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={800}>¿Este dispositivo ya estaba activado?</Typography>
            <Typography variant="body2" color="text.secondary">
              Captura la licencia, el teléfono registrado y un código vigente de Google Authenticator.
            </Typography>
            <TextField autoComplete="off"
              name="licenseGuid"
              label="Licencia ARSync"
              value={licenseGuidField.value}
              onChange={handleTenantActivationFormChange}
              error={licenseGuidField.error}
              fullWidth
            />
            <TextField autoComplete="off"
              name="adminPhone"
              label="Teléfono registrado"
              value={adminPhoneField.value}
              onChange={handleTenantActivationFormChange}
              error={adminPhoneField.error}
              fullWidth
              inputMode="tel"
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIphoneOutlinedIcon /></InputAdornment> }}
            />
            <TextField autoComplete="off"
              name="mfaCode"
              label="Código recibido por WhatsApp"
              value={mfaCodeField.value}
              onChange={handleTenantActivationFormChange}
              error={mfaCodeField.error}
              inputProps={{ maxLength: 6, inputMode: "numeric" }}
              fullWidth
            />
            <Button
              type="button"
              variant="outlined"
              startIcon={<SecurityOutlinedIcon />}
              onClick={handleRecoverMobileTenant}
              disabled={isRecoveringMobileTenant}
            >
              {isRecoveringMobileTenant ? "Validando..." : (mobileVerificationChallenge?.challengeId ? "Confirmar código y recuperar" : "Enviar código por WhatsApp")}
            </Button>
          </Stack>
        </Paper>
      )}

      {false && isMobileTenant && <Divider>o activa un dispositivo nuevo</Divider>}

      {isMobileTenant && (
        <TextField autoComplete="off"
          name="deviceAlias"
          label="Apodo o teléfono del dispositivo"
          value={deviceAliasField.value}
          onChange={handleTenantActivationFormChange}
          error={deviceAliasField.error}
          helperText={deviceAliasField.error ? "Captura un apodo o teléfono para identificar este dispositivo" : "Ejemplo: Teléfono ventas principal"}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIphoneOutlinedIcon /></InputAdornment> }}
        />
      )}

      {!isMobileTenant && (
        <TextField autoComplete="off"
          name="deviceAlias"
          label="Nombre de estación de trabajo"
          value={deviceAliasField.value}
          onChange={handleTenantActivationFormChange}
          error={deviceAliasField.error}
          helperText="Se usa el nombre de PrinterTools si lo dejas vacío"
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><DevicesOutlinedIcon /></InputAdornment> }}
        />
      )}

      <TextField
        name="licenseGuid"
        label="Licencia ARSync"
        value={licenseGuidField.value}
        onChange={handleTenantActivationFormChange}
        error={licenseGuidField.error}
        helperText={licenseGuidField.error ? "Formato requerido: ARXS-0000-0000-0000" : ""}
        fullWidth
        autoComplete="off"
        placeholder="ARXS-41D1-B501-C805"
        inputProps={{ maxLength: 19, spellCheck: false }}
        InputProps={{ startAdornment: <InputAdornment position="start"><KeyOutlinedIcon /></InputAdornment> }}
      />

      <TextField
        name="claveActivacion"
        label="Clave de aceptación del contrato"
        value={claveActivacionField.value}
        onChange={handleTenantActivationFormChange}
        error={claveActivacionField.error}
        helperText={claveActivacionField.error
          ? "Captura la clave enviada por correo tras aceptar el contrato"
          : "Solo aplica si la licencia fue invitada/reservada con contrato"}
        fullWidth
        autoComplete="off"
        InputProps={{ startAdornment: <InputAdornment position="start"><SecurityOutlinedIcon /></InputAdornment> }}
      />

      {!isMobileTenant && showPcActivationFields && <TextField autoComplete="off" name="companyName" label="Empresa" value={companyNameField.value} onChange={handleTenantActivationFormChange} error={companyNameField.error} helperText={companyNameField.error ? "Empresa requerida (méx. 30 caracteres)" : "Méx. 30 caracteres"} fullWidth inputProps={{ maxLength: 30 }} InputProps={{ startAdornment: <InputAdornment position="start"><ApartmentOutlinedIcon /></InputAdornment> }} />}
      {!isMobileTenant && showPcActivationFields && <TextField autoComplete="off" name="businessName" label="Razon social" value={businessNameField.value} onChange={handleTenantActivationFormChange} error={businessNameField.error} helperText={businessNameField.error ? "Razon social requerida" : ""} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><ApartmentOutlinedIcon /></InputAdornment> }} />}
      <TextField autoComplete="off" name="adminName" label={isMobileTenant ? "Nombre usuario" : "Administrador"} value={adminNameField.value} onChange={handleTenantActivationFormChange} error={adminNameField.error} helperText={adminNameField.error ? "Nombre requerido" : ""} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><BadgeOutlinedIcon /></InputAdornment> }} />
      <TextField name="adminEmail" label="Correo" value={adminEmailField.value} onChange={handleTenantActivationFormChange} error={adminEmailField.error} helperText={adminEmailField.error ? "Correo requerido" : ""} fullWidth autoComplete="off" InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon /></InputAdornment> }} />
      {!isMobileTenant && showPcActivationFields && <TextField autoComplete="off" name="adminPhone" label="Teléfono" value={adminPhoneField.value} onChange={handleTenantActivationFormChange} error={adminPhoneField.error} helperText={adminPhoneField.error ? "Teléfono requerido" : ""} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIphoneOutlinedIcon /></InputAdornment> }} />}
      {!isMobileTenant && showPcActivationFields && <TextField name="adminPassword" label="Password administrador" type="password" value={adminPasswordField.value} onChange={handleTenantActivationFormChange} error={adminPasswordField.error} helperText={adminPasswordField.error ? "Minimo 8 caracteres" : ""} fullWidth autoComplete="off" InputProps={{ startAdornment: <InputAdornment position="start"><PasswordOutlinedIcon /></InputAdornment> }} />}
      {!isMobileTenant && showPcActivationFields && <TextField name="adminPasswordConfirm" label="Confirmar password" type="password" value={adminPasswordConfirmField.value} onChange={handleTenantActivationFormChange} error={adminPasswordConfirmField.error} helperText={adminPasswordConfirmField.error ? "Las contraseñas no coinciden" : ""} fullWidth autoComplete="off" InputProps={{ startAdornment: <InputAdornment position="start"><PasswordOutlinedIcon /></InputAdornment> }} />}

      <Box sx={sx.actionRowEnd}>
        {!isMobileTenant && showPcActivationFields ? (
          <Button type="submit" variant="contained" startIcon={<VerifiedUserOutlinedIcon />} fullWidth>
            Activar licencia
          </Button>
        ) : (
          <>
            {/* 18/09/2026 | Se elimina Reintentar de la solicitud: solo Solicitar acceso. */}
            {!isMobileTenant && (
              <Button type="button" variant="contained" startIcon={<DevicesOutlinedIcon />} onClick={handleRequestWorkstationAccess}>
                Solicitar acceso
              </Button>
            )}
            {isMobileTenant && (
              <Button type="submit" variant="contained" startIcon={<VerifiedUserOutlinedIcon />}>
                Solicitar acceso
              </Button>
            )}
          </>
        )}
      </Box>

      {!isMobileTenant && !showPcActivationFields && (
        <>
          <Divider>o</Divider>
          <Button type="button" variant="outlined" startIcon={<VerifiedUserOutlinedIcon />} onClick={() => setShowPcActivationFields(true)} fullWidth>
            Activar licencia
          </Button>
        </>
      )}
    </Box>
  );

  const renderMfa = () => (
    <Box component="form" sx={sx.form} noValidate onSubmit={handleConfirmTenantMfa}>
      <Typography component="h2" variant="h5" fontWeight={800}>
        Confirmar Google Authenticator
      </Typography>
      <Typography component="p" sx={sx.caption}>
        {tenantMfaSetup?.otpAuthUri
          ? "Escanea el código QR y captura el código de 6 dígitos."
          : "Captura el código vigente de 6 dígitos de Google Authenticator para autorizar este dispositivo."}
      </Typography>

      {mfaQrDataUrl && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <img src={mfaQrDataUrl} alt="Código QR MFA" style={{ width: 210, maxWidth: "100%" }} />
        </Box>
      )}

      {tenantMfaSetup?.manualEntryKey && (
        <Alert severity="info">
          Clave manual: {tenantMfaSetup.manualEntryKey}
        </Alert>
      )}
      <TextField
        name="mfaCode"
        label="Código MFA"
        value={mfaCodeField.value}
        onChange={handleTenantActivationFormChange}
        error={mfaCodeField.error}
        helperText={mfaCodeField.error ? "Captura 6 dígitos" : ""}
        fullWidth
        autoComplete="one-time-code"
        inputProps={{ maxLength: 6 }}
      />

      <Box sx={sx.actionRowEnd}>
        <Button type="button" variant="outlined" startIcon={<RestartAltOutlinedIcon />} onClick={handleResetTenantActivation}>
          Reiniciar
        </Button>
        <Button type="submit" variant="contained" startIcon={<VerifiedUserOutlinedIcon />}>
          Confirmar MFA
        </Button>
      </Box>
    </Box>
  );

  const renderRedeem = () => (
    <Box sx={sx.statusBlock}>
      <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#2e7d32" }} />
      <Typography component="h2" variant="h5" fontWeight={800}>
        Cuenta verificada
      </Typography>
      <Typography component="p" color="text.secondary">
        Ya se confirmó MFA. Ahora registra este equipo con la licencia.
      </Typography>

      {tenantGateMessage && (
        <Alert severity="success" sx={{ width: "100%" }}>
          {tenantGateMessage}
        </Alert>
      )}

      <Box sx={sx.actionRow}>
        <Button variant="outlined" startIcon={<RestartAltOutlinedIcon />} onClick={handleResetTenantActivation}>
          Reiniciar
        </Button>
        <Button variant="contained" startIcon={<VerifiedUserOutlinedIcon />} onClick={handleRedeemTenantActivation} disabled={isRedeemingTenant}>
          {isRedeemingTenant ? "Registrando..." : "Registrar equipo"}
        </Button>
      </Box>
    </Box>
  );

  const renderReassign = () => (
    <Box sx={sx.statusBlock}>
      <ErrorOutlineIcon sx={{ fontSize: 54, color: "#e65100" }} />
      <Typography component="h2" variant="h5" fontWeight={800}>
        Equipo registrado en otro dispositivo
      </Typography>
      <Typography component="p" color="text.secondary">
        {tenantGateMessage || "La llave de este equipo ya está registrada en otro dispositivo."}
      </Typography>

      {tenantCorrelationId && (
        <Alert severity="warning" sx={{ width: "100%" }}>
          Folio: {tenantCorrelationId}
        </Alert>
      )}

      <Alert severity="info" sx={{ width: "100%" }}>
        Si el equipo original ya no esté disponible, puedes reasignar la licencia a este equipo.
      </Alert>

      <Box sx={sx.actionRow}>
        <Button variant="contained" startIcon={<VerifiedUserOutlinedIcon />} onClick={handleReassignTenantDevice}>
          Recuperar equipo
        </Button>
        <Button variant="outlined" startIcon={<RestartAltOutlinedIcon />} onClick={handleResetTenantActivation}>
          Nueva activación
        </Button>
      </Box>
    </Box>
  );

  const renderProvisioningPending = () => (
    <Box sx={sx.statusBlock}>
      <HourglassEmptyOutlinedIcon sx={{ fontSize: 52, color: "#e65100" }} />
      <Typography component="h2" variant="h5" fontWeight={800}>
        {getTitle(tenantGateStatus)}
      </Typography>
      <Typography component="p" color="text.secondary">
        {tenantGateMessage || "Base de datos empresa en provisionamiento."}
      </Typography>

      <Alert severity="info" sx={{ width: "100%" }}>
        La base de datos del cliente SizeSoftX esté siendo creada. Una vez completada, presiona Reintentar.
      </Alert>

      <Box sx={sx.actionRow}>
        <Button variant="contained" startIcon={<PublishedWithChangesOutlinedIcon />} onClick={handleRetryTenantGate}>
          Reintentar
        </Button>
        <Button variant="outlined" startIcon={<RestartAltOutlinedIcon />} onClick={handleResetTenantActivation}>
          Cancelar
        </Button>
      </Box>
    </Box>
  );

  const renderPendingApproval = () => (
    <Box sx={sx.statusBlock}>
      <HourglassEmptyOutlinedIcon sx={{ fontSize: 52, color: "#e65100" }} />
      <Typography component="h2" variant="h5" fontWeight={800}>
        Solicitud de acceso pendiente
      </Typography>
      <Typography component="p" color="text.secondary">
        {tenantGateMessage || "El propietario de la licencia debe aprobar este dispositivo desde el dashboard administrativo."}
      </Typography>

      <Alert severity="info" sx={{ width: "100%" }}>
        Una vez aprobado, presiona Reintentar para continuar.
      </Alert>

      <Box sx={sx.actionRow}>
        <Button variant="contained" startIcon={<PublishedWithChangesOutlinedIcon />} onClick={handleRetryTenantGate}>
          Reintentar
        </Button>
        <Button variant="outlined" startIcon={<RestartAltOutlinedIcon />} onClick={handleResetTenantActivation}>
          Cancelar solicitud
        </Button>
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (isLoading) return renderBootstrap();
    if (showPrinterTools) return renderPrinterTools();
    if (showPendingApproval) return renderPendingApproval();
    if (showActivation) return renderActivation();
    if (showMfa) return renderMfa();
    if (showRedeem) return renderRedeem();
    if (showReassign) return renderReassign();
    if (showDenied) return renderDenied();
    if (tenantGateStatus === tenantGateStatuses.PENDING) return renderProvisioningPending();
    return renderBootstrap();
  };
  const renderDebugLog = () => (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={800}>
          Log diagnostico temporal
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="contained" onClick={copyTenantDebugLog}>
            Copiar log
          </Button>
          <Button size="small" variant="outlined" color="warning" onClick={clearTenantDebugLog}>
            Limpiar
          </Button>
        </Stack>
      </Stack>
      <TextField autoComplete="off"
        fullWidth
        multiline
        minRows={8}
        maxRows={18}
        value={tenantDebugLog || "Sin eventos registrados todavia."}
        InputProps={{ readOnly: true }}
        sx={{
          "& textarea": {
            fontFamily: "Consolas, 'Courier New', monospace",
            fontSize: 12,
            lineHeight: 1.45,
            whiteSpace: "pre",
          },
          "& .MuiInputBase-root": {
            alignItems: "flex-start",
            backgroundColor: "#0b1020",
            color: "#e6edf3",
          },
        }}
      />
    </Box>
  );

  return (
    <Box sx={sx.shell}>
      <Box sx={sx.brand}>
        <Box sx={sx.brandTop}>
          <Box component="img" src={arsyncLogo} alt="ARSync" sx={sx.brandLogo} />
          <Box>
            <Typography variant="h5" fontWeight={900}>
              SizeSoftX
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              ARSync Security
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="h3" fontWeight={900} sx={{ mb: 2 }}>
            Acceso
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.82, lineHeight: 1.7 }}>
            Validación previa al Login. El sistema revisa PrinterTools, equipo y licencia antes de abrir SizeSoftX.
          </Typography>
        </Box>

        <Box sx={sx.badgeWrap}>
          <Chip icon={<SecurityOutlinedIcon />} label="Seguridad" variant="outlined" sx={sx.badge} />
          <Chip icon={<VerifiedUserOutlinedIcon />} label="Licencia activa" variant="outlined" sx={sx.badge} />
          <Chip icon={<CheckCircleOutlineIcon />} label="Equipo autorizado" variant="outlined" sx={sx.badge} />
        </Box>
      </Box>

      <Box sx={sx.workspace}>
        <Paper elevation={0} sx={sx.panel}>
          <Box component="img" src={arsyncLogo} alt="ARSync" sx={sx.panelLogo} />
          <Typography component="h1" variant="h5" fontWeight={900} textAlign="center" sx={{ mb: 0.5 }}>
            {getTitle(tenantGateStatus)}
          </Typography>
          <Divider sx={{ width: "100%", mb: 1.5 }} />
          {renderContent()}
          {false && (isMobileTenant || tenantDebugLog) && renderDebugLog()}
        </Paper>
      </Box>
    </Box>
  );
}
